import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from "react-redux";
import {fetchApplicationTypes, fetchFields, sendFields, updateApplication, setFulledFields, fetchApplicationById, fetchDraftById, saveDraft, updateDraft, setDraftId, clearDraft} from "./declarationSlice";
import styles from "./Decloration.module.css";
import Button from "@mui/material/Button";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import TextFields from "./TextFields";
import DeclarationReview from "./DeclarationReview";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import debounce from 'lodash.debounce';
import moment from "moment/moment";
import {attachmentList, draftValuesForSave, mergeDraftValues} from "./fileAttachment";
import {
    filterApplicationFieldBlocks,
    getExcludedApplicationFieldIds,
} from "../../utils/applicationFieldFilters";

const normalizeDateToRu = (value) => {
    if (value == null) return value;
    if (typeof value !== 'string') return value;

    const v = value.trim();
    if (!v) return v;

    // Already in expected format
    if (/^(0[1-9]|[12]\d|3[01])\.(0[1-9]|1[0-2])\.(19|20)\d{2}$/.test(v)) {
        return v;
    }

    // ISO date: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss...
    const isoMatch = v.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
    if (isoMatch) {
        const [, yyyy, mm, dd] = isoMatch;
        return `${dd}.${mm}.${yyyy}`;
    }

    // Compact date: YYYYMMDD
    const compact = v.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (compact) {
        const [, yyyy, mm, dd] = compact;
        return `${dd}.${mm}.${yyyy}`;
    }

    // Dotted ISO-ish: YYYY.MM.DD
    const dottedIso = v.match(/^(\d{4})\.(\d{2})\.(\d{2})$/);
    if (dottedIso) {
        const [, yyyy, mm, dd] = dottedIso;
        return `${dd}.${mm}.${yyyy}`;
    }

    return value;
};

// Normalize period field value: convert object {from, to} to JSON string
const normalizePeriodValue = (value, fieldType) => {
    if (!value) return value;
    
    // If it's already a JSON string, validate and return as is
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            // Validate it has the expected structure
            if (parsed && typeof parsed === 'object' && ('from' in parsed || 'to' in parsed)) {
                return value;
            }
        } catch {
            // Not valid JSON, might be something else - return as is
            return value;
        }
    }
    
    // If it's an object with from/to, convert to JSON string
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        if (value.from || value.to) {
            return JSON.stringify({ 
                from: value.from || '', 
                to: value.to || '' 
            });
        }
    }
    
    return value;
};

/** Some gateways wrap JSON as { data: { ... } } */
const normalizeApplicationPayload = (payload) => {
    if (payload == null) return null;
    if (
        typeof payload === 'object' &&
        payload !== null &&
        Object.prototype.hasOwnProperty.call(payload, 'data') &&
        payload.data != null &&
        typeof payload.data === 'object' &&
        !Array.isArray(payload.data)
    ) {
        return payload.data;
    }
    return payload;
};

/**
 * Comment left by admin when setting status to «на доработку».
 * Backend field name may vary — extend this list when API is fixed.
 */
const getAdminRevisionComment = (app) => {
    if (!app || typeof app !== 'object') return null;

    const directKeys = [
        'revision_comment',
        'revision_comment_text',
        'admin_comment',
        'admin_comment_text',
        'status_comment',
        'status_change_comment',
        'last_status_comment',
        'comment_from_admin',
        'moderator_comment',
        'rejection_reason',
    ];

    for (const key of directKeys) {
        const v = app[key];
        if (v != null && String(v).trim()) return String(v).trim();
    }

    if (app.status && typeof app.status === 'object' && typeof app.status.comment === 'string' && app.status.comment.trim()) {
        return app.status.comment.trim();
    }
    if (app.last_status_change?.comment) {
        const c = app.last_status_change.comment;
        if (typeof c === 'string' && c.trim()) return c.trim();
    }

    return null;
};

function Declaration(props) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const id = useParams().id;
    const [currentPage, setCurrentPage] = useState(0);
    const [showReview, setShowReview] = useState(false);
    const fields = useSelector(state => state.declarationSlice.fields);
    const loading = useSelector(state => state.declarationSlice.loading);
    const error = useSelector(state => state.declarationSlice.error);
    const draftId = useSelector(state => state.declarationSlice.draftId);
    const lastSavedAt = useSelector(state => state.declarationSlice.lastSavedAt);
    const isSavingDraft = useSelector(state => state.declarationSlice.isSavingDraft);
    const [fieldValues, setFieldValues] = useState({});
    const excludedFieldIds = useMemo(
        () => getExcludedApplicationFieldIds(fields?.applicationType?.active_field_blocks || []),
        [fields?.applicationType?.active_field_blocks],
    );
    const activeFieldBlocks = useMemo(
        () => filterApplicationFieldBlocks(fields?.applicationType?.active_field_blocks),
        [fields?.applicationType?.active_field_blocks],
    );
    const totalPages = activeFieldBlocks ? activeFieldBlocks.length : 0;
    const currentPageData = activeFieldBlocks ? activeFieldBlocks[currentPage] : null;
    const [allFieldsFilled, setAllFieldsFilled] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
    const [prefillSource, setPrefillSource] = useState(null); // 'draft' | 'copy' | 'edit' | null
    const [originalApplicationId, setOriginalApplicationId] = useState(null); // ID of application being edited
    const [existingFileUrls, setExistingFileUrls] = useState({}); // Map of field_id -> file_url for existing files
    /** Admin comment when status was set to «на доработку» (from GET /applications/:id) */
    const [revisionAdminComment, setRevisionAdminComment] = useState(null);

    // Auto-save function
    const performAutoSave = useCallback(async (values, currentPageNum) => {
        // Don't auto-save drafts when editing an existing application
        if (!id || !autoSaveEnabled || originalApplicationId) return false;
        
        try {
            const draftData = {
                application_type_id: parseInt(id),
                field_values: draftValuesForSave(values),
                current_page: currentPageNum,
                is_draft: true
            };
            
            if (draftId) {
                await dispatch(updateDraft({ draftId, draftData })).unwrap();
                return true;
            } else {
                const result = await dispatch(saveDraft(draftData)).unwrap();
                if (result?.id) {
                    dispatch(setDraftId(result.id));
                    // Update URL without reload, but preserve editFrom if present
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.set('draftId', result.id);
                    // Preserve editFrom parameter if it exists
                    const editFromId = searchParams.get('editFrom');
                    if (editFromId) {
                        newSearchParams.set('editFrom', editFromId);
                    }
                    navigate(`/declarations/${id}?${newSearchParams.toString()}`, { replace: true });
                    return true;
                }
                return false;
            }
        } catch (error) {
            console.error('Auto-save failed:', error);
            return false;
        }
    }, [id, draftId, autoSaveEnabled, originalApplicationId, dispatch, navigate, searchParams]);

    // Debounced auto-save
    const debouncedAutoSave = useMemo(
        () => debounce(performAutoSave, 2000),
        [performAutoSave]
    );

    // Load draft, copied, or edited application on mount
    // Priority: editFrom > draftId > copyFrom
    useEffect(() => {
        const draftIdParam = searchParams.get('draftId');
        const copyFromId = searchParams.get('copyFrom');
        const editFromId = searchParams.get('editFrom');

        setRevisionAdminComment(null);

        // Priority 1: Editing existing application (на доработку) - highest priority
        if (editFromId && id) {
            // Clean up URL: remove draftId if present when editing
            if (draftIdParam) {
                const cleanParams = new URLSearchParams(searchParams);
                cleanParams.delete('draftId');
                cleanParams.set('editFrom', editFromId);
                navigate(`/declarations/${id}?${cleanParams.toString()}`, { replace: true });
            }
            
            setPrefillSource('edit');
            setOriginalApplicationId(parseInt(editFromId));
            
            console.log('Loading application for editing:', editFromId);
            dispatch(fetchApplicationById(editFromId))
                .unwrap()
                .then((raw) => {
                    const app = normalizeApplicationPayload(raw);
                    console.log('Application data (edit):', app);
                    if (!app) return;

                    const comment = getAdminRevisionComment(app);
                    // Show any non-empty revision comment returned for this application (edit flow is only for «на доработку»)
                    setRevisionAdminComment(comment || null);
                    const editedValues = {};
                    const fileUrls = {};
                    
                    // Pre-populate all fields including file URLs
                    if (app.field_values && Array.isArray(app.field_values)) {
                        console.log('Processing field_values as array:', app.field_values);
                        app.field_values.forEach(fv => {
                            if (fv.field_id) {
                                if (fv.file_url) {
                                    // Store file URL separately for existing files
                                    fileUrls[fv.field_id] = fv.file_url;
                                    // Store a placeholder object to indicate file exists
                                    editedValues[fv.field_id] = { 
                                        url: fv.file_url,
                                        name: fv.file_name || 'Прикрепленный файл',
                                        isExisting: true 
                                    };
                                } else if (fv.value && typeof fv.value === 'object' && fv.value.from && fv.value.to) {
                                    // Period field: normalize to JSON string
                                    editedValues[fv.field_id] = normalizePeriodValue(fv.value, 'period');
                                } else {
                                    editedValues[fv.field_id] = fv.value;
                                }
                            }
                        });
                    } else if (app.field_values && typeof app.field_values === 'object') {
                        console.log('Processing field_values as object:', app.field_values);
                        Object.keys(app.field_values).forEach(key => {
                            const value = app.field_values[key];
                            // Extract field ID from key (e.g., "field_17" -> 17)
                            const fieldIdMatch = key.match(/^field_(\d+)$/);
                            const fieldId = fieldIdMatch ? parseInt(fieldIdMatch[1]) : null;
                            
                            if (Array.isArray(value) && value.length > 0) {
                                const normalized = value.map(v =>
                                    typeof v === 'string'
                                        ? { url: v, name: 'Прикрепленный файл', isExisting: true }
                                        : { url: v?.file_url || v?.url, name: v?.file_name || v?.name || 'Прикрепленный файл', isExisting: true }
                                );
                                editedValues[fieldId || key] = normalized;
                                if (normalized[0]?.url) fileUrls[fieldId || key] = normalized[0].url;
                            } else if (value && typeof value === 'object' && value.file_url) {
                                fileUrls[fieldId || key] = value.file_url;
                                editedValues[fieldId || key] = {
                                    url: value.file_url,
                                    name: value.file_name || 'Прикрепленный файл',
                                    isExisting: true
                                };
                            } else if (value && typeof value === 'object' && value.from && value.to) {
                                // Period field: normalize to JSON string
                                editedValues[fieldId || key] = normalizePeriodValue(value, 'period');
                            } else if (value && typeof value !== 'object' && !(value instanceof File)) {
                                editedValues[fieldId || key] = value;
                            } else if (value && typeof value === 'object' && !value.file_url && !value.from) {
                                // Other object types - might be period in different format
                                editedValues[fieldId || key] = normalizePeriodValue(value, 'period');
                            }
                        });
                    } else {
                        console.warn('Unexpected field_values format:', app.field_values);
                    }
                    
                    console.log('Setting fieldValues:', editedValues);
                    console.log('Setting existingFileUrls:', fileUrls);
                    setFieldValues(editedValues);
                    setExistingFileUrls(fileUrls);
                })
                .catch((error) => {
                    console.error('Failed to fetch application for editing:', error);
                    window.showAlert({
                        title: error?.message || 'Не удалось загрузить заявление для редактирования',
                        type: 'error'
                    });
                    navigate('/declaration');
                });
        }
        // Priority 2: Loading draft
        else if (draftIdParam && id) {
            setPrefillSource('draft');
            dispatch(fetchDraftById(draftIdParam)).then((result) => {
                if (result.payload) {
                    const draft = result.payload;
                    // Verify that the draft's application_type_id matches the URL id
                    if (draft.application_type_id && parseInt(draft.application_type_id) !== parseInt(id)) {
                        console.warn('Draft application_type_id does not match URL id');
                        window.showAlert({
                            title: 'Ошибка: тип заявления черновика не соответствует выбранному типу',
                            type: 'error'
                        });
                        navigate('/declaration');
                        return;
                    }
                    if (draft.field_values) {
                        // Handle both object and array formats.
                        // Keep files the user just picked: a draft save cannot store File objects,
                        // and reloading the draft would otherwise replace them with empty records.
                        let incoming = {};
                        if (Array.isArray(draft.field_values)) {
                            draft.field_values.forEach(fv => {
                                if (fv.field_id) {
                                    incoming[fv.field_id] = fv.value;
                                }
                            });
                        } else if (typeof draft.field_values === 'object') {
                            incoming = draft.field_values;
                        }
                        setFieldValues(prev => mergeDraftValues(prev, incoming));
                    }
                    if (draft.current_page !== undefined && draft.current_page !== null) {
                        setCurrentPage(draft.current_page);
                    }
                } else if (result.error) {
                    window.showAlert({
                        title: 'Не удалось загрузить черновик',
                        type: 'error'
                    });
                    navigate('/declaration');
                }
            });
        }
        // Priority 3: Copying application
        else if (copyFromId) {
            setPrefillSource('copy');
            dispatch(fetchApplicationById(copyFromId)).then((result) => {
                if (result.payload) {
                    const app = result.payload;
                    // Pre-populate fields (excluding files - user must re-upload)
                    const copiedValues = {};
                    if (app.field_values && Array.isArray(app.field_values)) {
                        app.field_values.forEach(fv => {
                            if (fv.field_id && !fv.file_url) {
                                copiedValues[fv.field_id] = fv.value;
                            }
                        });
                    } else if (app.field_values && typeof app.field_values === 'object') {
                        Object.keys(app.field_values).forEach(key => {
                            const value = app.field_values[key];
                            if (value && typeof value !== 'object' && !(value instanceof File)) {
                                copiedValues[key] = value;
                            }
                        });
                    }
                    setFieldValues(copiedValues);
                }
            });
        }
    }, [dispatch, searchParams, id, navigate]);

    // Normalize date fields for drafts/copies/edits:
    // backend may store dates as ISO (YYYY-MM-DD), but UI expects DD.MM.YYYY mask.
    useEffect(() => {
        const blocks = activeFieldBlocks;
        if (!blocks || !Object.keys(fieldValues || {}).length) return;
        if (prefillSource !== 'draft' && prefillSource !== 'copy' && prefillSource !== 'edit') return;

        let changed = false;
        const next = { ...fieldValues };

        blocks.forEach((block) => {
            (block?.active_fields || []).forEach((f) => {
                if (f?.field_type?.type !== 'date') return;
                const key = f.id;
                const current = next[key];
                const normalized = normalizeDateToRu(current);
                if (normalized !== current) {
                    next[key] = normalized;
                    changed = true;
                }
            });
        });

        if (changed) {
            setFieldValues(next);
        }
    }, [activeFieldBlocks, fieldValues, prefillSource]);

    useEffect(() => {
        if (id) {
            console.log('Fetching fields for application type:', id);
            dispatch(fetchFields({ id })).then((result) => {
                console.log('Fields fetch result:', result);
                if (result.error) {
                    console.error('Failed to fetch fields:', result.error);
                    window.showAlert({
                        title: 'Не удалось загрузить форму заявления',
                        type: 'error'
                    });
                }
            }).catch((error) => {
                console.error('Failed to fetch fields:', error);
                window.showAlert({
                    title: 'Не удалось загрузить форму заявления',
                    type: 'error'
                });
            });
            dispatch(fetchApplicationTypes());
        }
    }, [id, dispatch]);

    const handleInputChange = (event, fieldId) => {
        const { value } = event.target;
        const newFieldValues = {
            ...fieldValues,
            [fieldId]: value
        };
        setFieldValues(newFieldValues);
        
        // Trigger auto-save
        if (autoSaveEnabled) {
            debouncedAutoSave(newFieldValues, currentPage);
        }
    };

    const inputFileChange = (event, field) => {
        const fieldId = field?.id;
        if (fieldId == null) return;
        const newValue = event.target?.value;
        if (newValue === undefined) return;
        const newFieldValues = { ...fieldValues, [fieldId]: newValue };
        setFieldValues(newFieldValues);
        if (existingFileUrls[fieldId]) {
            const next = { ...existingFileUrls };
            delete next[fieldId];
            setExistingFileUrls(next);
        }
        if (autoSaveEnabled) {
            debouncedAutoSave(newFieldValues, currentPage);
        }
    };

    const handlePreviousPage = () => {
        const newPage = Math.max(0, currentPage - 1);
        setCurrentPage(newPage);
        // Auto-save when changing pages
        if (autoSaveEnabled) {
            debouncedAutoSave(fieldValues, newPage);
        }
    };

    const validateCurrentPage = () => {
        if (!currentPageData || !currentPageData.active_fields) {
            return { isValid: false, errors: {} };
        }

        let isValid = true;
        const updatedFieldErrors = {};

        const mandatoryFileNames = ['Чек ККТ', 'Договор/спортивный абонемент'];

        currentPageData.active_fields.forEach((field) => {
            const fieldValue = fieldValues[field.id];
            
            const isFileField = field.field_type.type === 'file';
            const fileFieldRequired = mandatoryFileNames.includes(field.label || field.name);
            const isFieldRequired = isFileField ? fileFieldRequired : true;

            // Check if field is required and empty
            if (isFieldRequired) {
                if (isFileField && field.is_multiple) {
                    const hasValid = attachmentList(fieldValue).length > 0;
                    if (!hasValid) {
                        updatedFieldErrors[field.id] = 'Необходимо прикрепить хотя бы один файл';
                        isValid = false;
                    }
                } else if (!fieldValue ||
                    (typeof fieldValue === 'string' && fieldValue.trim() === '') ||
                    (isFileField && (!fieldValue || (!fieldValue.name && !(fieldValue.isExisting && fieldValue.url))))) {
                    updatedFieldErrors[field.id] = 'Поле должно быть заполнено';
                    isValid = false;
                }
            }
            
            // Additional validation for specific field types
            if (fieldValue && field.field_type.type === 'email') {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(fieldValue)) {
                    updatedFieldErrors[field.id] = 'Введите корректный email';
                    isValid = false;
                }
            }
            
            if (fieldValue && field.field_type.type === 'tel') {
                const phoneRegex = /^\+7 \(\d{3}\) \d{3} \d{2} \d{2}$/;
                if (!phoneRegex.test(fieldValue)) {
                    updatedFieldErrors[field.id] = 'Введите корректный номер телефона';
                    isValid = false;
                }
            }

            // Date validation: expect DD.MM.YYYY
            if (fieldValue && field.field_type.type === 'date') {
                const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.(19|20)\d{2}$/;
                if (!dateRegex.test(fieldValue)) {
                    updatedFieldErrors[field.id] = 'Введите дату в формате ДД.ММ.ГГГГ';
                    isValid = false;
                }
            }

            // Period validation: expect JSON with from and to dates in YYYY-MM-DD format
            if (field.field_type.type === 'period') {
                if (!fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
                    updatedFieldErrors[field.id] = 'Поле должно быть заполнено';
                    isValid = false;
                } else {
                    try {
                        const period = typeof fieldValue === 'string' ? JSON.parse(fieldValue) : fieldValue;
                        if (!period.from || !period.to) {
                            updatedFieldErrors[field.id] = 'Заполните обе даты периода';
                            isValid = false;
                        } else {
                            // Validate date format YYYY-MM-DD
                            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
                            if (!dateRegex.test(period.from) || !dateRegex.test(period.to)) {
                                updatedFieldErrors[field.id] = 'Неверный формат дат периода';
                                isValid = false;
                            } else if (new Date(period.from) > new Date(period.to)) {
                                updatedFieldErrors[field.id] = 'Дата начала не может быть позже даты окончания';
                                isValid = false;
                            }
                        }
                    } catch (e) {
                        updatedFieldErrors[field.id] = 'Неверный формат периода';
                        isValid = false;
                    }
                }
            }
        });

        return { isValid, errors: updatedFieldErrors };
    };

    // Validate all pages (used when skipping review in edit mode)
    const validateAllPages = () => {
        if (!fields?.applicationType || !activeFieldBlocks?.length) {
            return { isValid: false, errors: {} };
        }

        let isValid = true;
        const allErrors = {};

        activeFieldBlocks.forEach((block) => {
            if (!block.active_fields) return;
            
            block.active_fields.forEach((field) => {
                const fieldValue = fieldValues[field.id];
                const mandatoryFileNames = ['Чек ККТ', 'Договор/спортивный абонемент'];
                const isFileField = field.field_type.type === 'file';
                const fileFieldRequired = mandatoryFileNames.includes(field.label || field.name);
                const isFieldRequired = isFileField ? fileFieldRequired : true;

                // Check if field is required and empty
                if (isFieldRequired) {
                    if (isFileField && field.is_multiple) {
                        const hasValid = attachmentList(fieldValue).length > 0;
                        if (!hasValid) {
                            allErrors[field.id] = 'Необходимо прикрепить хотя бы один файл';
                            isValid = false;
                        }
                    } else if (!fieldValue ||
                        (typeof fieldValue === 'string' && fieldValue.trim() === '') ||
                        (isFileField && (!fieldValue || (!fieldValue.name && !(fieldValue.isExisting && fieldValue.url))))) {
                        allErrors[field.id] = 'Поле должно быть заполнено';
                        isValid = false;
                    }
                }
                
                // Additional validation for specific field types
                if (fieldValue && field.field_type.type === 'email') {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(fieldValue)) {
                        allErrors[field.id] = 'Введите корректный email';
                        isValid = false;
                    }
                }
                
                if (fieldValue && field.field_type.type === 'tel') {
                    const phoneRegex = /^\+7 \(\d{3}\) \d{3} \d{2} \d{2}$/;
                    if (!phoneRegex.test(fieldValue)) {
                        allErrors[field.id] = 'Введите корректный номер телефона';
                        isValid = false;
                    }
                }

                // Date validation: expect DD.MM.YYYY
                if (fieldValue && field.field_type.type === 'date') {
                    const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.(19|20)\d{2}$/;
                    if (!dateRegex.test(fieldValue)) {
                        allErrors[field.id] = 'Введите дату в формате ДД.ММ.ГГГГ';
                        isValid = false;
                    }
                }

                // Period validation: expect JSON with from and to dates in YYYY-MM-DD format
                if (field.field_type.type === 'period') {
                    if (!fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
                        allErrors[field.id] = 'Поле должно быть заполнено';
                        isValid = false;
                    } else {
                        try {
                            const period = typeof fieldValue === 'string' ? JSON.parse(fieldValue) : fieldValue;
                            if (!period.from || !period.to) {
                                allErrors[field.id] = 'Заполните обе даты периода';
                                isValid = false;
                            } else {
                                // Validate date format YYYY-MM-DD
                                const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
                                if (!dateRegex.test(period.from) || !dateRegex.test(period.to)) {
                                    allErrors[field.id] = 'Неверный формат дат периода';
                                    isValid = false;
                                } else if (new Date(period.from) > new Date(period.to)) {
                                    allErrors[field.id] = 'Дата начала не может быть позже даты окончания';
                                    isValid = false;
                                }
                            }
                        } catch (e) {
                            allErrors[field.id] = 'Неверный формат периода';
                            isValid = false;
                        }
                    }
                }
            });
        });

        return { isValid, errors: allErrors };
    };

    const handleNextPage = async () => {
        // Clear previous errors
        setSubmitError(null);
        
        const validation = validateCurrentPage();
        setFieldErrors(validation.errors);

        if (!validation.isValid) {
            window.showAlert({ 
                title: 'Пожалуйста, заполните все обязательные поля корректно', 
                type: 'error' 
            });
            return;
        }

        // If this is the last page
        if (currentPage === totalPages - 1) {
            // Ensure fields are loaded
            if (!fields?.applicationType || !activeFieldBlocks?.length) {
                window.showAlert({ 
                    title: 'Данные формы еще загружаются. Пожалуйста, подождите...', 
                    type: 'warning' 
                });
                return;
            }
            
            // When editing: skip review and submit directly
            if (originalApplicationId) {
                // Validate all pages before submitting
                const allPagesValidation = validateAllPages();
                if (!allPagesValidation.isValid) {
                    setFieldErrors(allPagesValidation.errors);
                    window.showAlert({ 
                        title: 'Пожалуйста, заполните все обязательные поля корректно', 
                        type: 'error' 
                    });
                    // Go to first page with error
                    const firstErrorPage = activeFieldBlocks.findIndex(block => 
                        block.active_fields?.some(field => allPagesValidation.errors[field.id])
                    );
                    if (firstErrorPage >= 0) {
                        setCurrentPage(firstErrorPage);
                    }
                    return;
                }
                // Submit directly without review
                await handleSubmitForm();
            } else {
                // For new applications: show review
                setShowReview(true);
            }
        } else {
            // Move to next page
            const newPage = Math.min(currentPage + 1, totalPages - 1);
            setCurrentPage(newPage);
            dispatch(setFulledFields(fieldValues));
            // Auto-save when changing pages
            if (autoSaveEnabled) {
                debouncedAutoSave(fieldValues, newPage);
            }
        }
    };

    const handleSubmitForm = async () => {
        if (!fields?.applicationType) {
            setSubmitError('Ошибка: данные формы не загружены');
            return;
        }

        setIsSubmitting(true);
        setSubmitError(null);
        setAutoSaveEnabled(false); // Disable auto-save during submission

        try {
            const formData = new FormData();
            formData.append('application_type_id', fields.applicationType.id);

            const fieldIdToMeta = {};
            (activeFieldBlocks || []).forEach(block => {
                (block.active_fields || []).forEach(f => { fieldIdToMeta[f.id] = f; });
            });
            
            // Add all field values to FormData
            Object.keys(fieldValues).forEach(fieldId => {
                if (excludedFieldIds.has(String(fieldId))) {
                    return;
                }
                const value = fieldValues[fieldId];
                const isMultiple = fieldIdToMeta[fieldId]?.is_multiple;
                const arr = Array.isArray(value) ? value : null;

                if (isMultiple && arr && arr.length > 0) {
                    arr.forEach(item => {
                        if (item && typeof item === 'object' && item.isExisting && item.url) {
                            formData.append(`${fieldId}[]`, item.url);
                        } else if (item instanceof File) {
                            formData.append(`${fieldId}[]`, item);
                        }
                    });
                    return;
                }

                // Handle existing files (from edit mode): backend expects field id as key, URL string as value
                if (value && typeof value === 'object' && value.isExisting && value.url) {
                    formData.append(fieldId, value.url);
                }
                // Handle new file uploads
                else if (value instanceof File) {
                    formData.append(fieldId, value);
                }
                // Handle regular field values
                else if (value !== null && value !== undefined && value !== '') {
                    if (!(typeof value === 'object' && value.isExisting)) {
                        formData.append(fieldId, value);
                    }
                }
            });

            let result;
            if (originalApplicationId) {
                // Update existing application (edit mode)
                result = await dispatch(updateApplication({ 
                    applicationId: originalApplicationId, 
                    data: formData 
                })).unwrap();
                
                window.showAlert({ 
                    title: 'Заявление успешно обновлено!', 
                    type: 'success' 
                });
            } else {
                // Create new application
                result = await dispatch(sendFields(formData)).unwrap();
                
                window.showAlert({ 
                    title: 'Заявление успешно отправлено!', 
                    type: 'success' 
                });
            }
            
            if (result) {
                // Clear draft after successful submission
                if (draftId) {
                    dispatch(clearDraft());
                }
                
                navigate('/declaration/success');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            
            let errorMessage = originalApplicationId 
                ? 'Произошла ошибка при обновлении заявления'
                : 'Произошла ошибка при отправке заявления';
            
            if (error?.message === 'Network Error') {
                errorMessage = 'Не удалось отправить файлы. Проверьте интернет и попробуйте ещё раз';
            } else if (error?.message) {
                errorMessage = error.message;
            } else if (error?.status === 422) {
                errorMessage = 'Проверьте правильность заполнения полей';
            } else if (error?.status === 401) {
                errorMessage = 'Необходимо авторизоваться';
            } else if (error?.status >= 500) {
                errorMessage = 'Ошибка сервера. Попробуйте позже';
            }
            
            setSubmitError(errorMessage);
            setAutoSaveEnabled(true); // Re-enable auto-save on error
            window.showAlert({ 
                title: errorMessage, 
                type: 'error' 
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Manual save draft handler
    const handleSaveDraft = async () => {
        try {
            // In «edit existing application» mode («на доработку») we do not create/update drafts.
            // So for «Сохранить» UX we just move to next page (same as «Далее»).
            const canGoNext = currentPage < totalPages - 1;
            const targetPage = canGoNext ? currentPage + 1 : currentPage;

            if (originalApplicationId) {
                dispatch(setFulledFields(fieldValues));
                setCurrentPage(targetPage);
                return;
            }

            // Avoid duplicate requests: cancel any pending debounced save and do one explicit save.
            debouncedAutoSave.cancel();

            const ok = await performAutoSave(fieldValues, targetPage);
            // Even if draft saving fails, don't block the user from continuing the form.
            if (targetPage !== currentPage) setCurrentPage(targetPage);

            if (!ok) {
                window.showAlert({
                    title: 'Ошибка при сохранении черновика',
                    type: 'error',
                });
            }
        } catch (error) {
            console.error('Manual save failed:', error);
            window.showAlert({
                title: 'Ошибка при сохранении черновика',
                type: 'error',
            });
        }
    };

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedAutoSave.cancel();
        };
    }, [debouncedAutoSave]);

    const handleEditFromReview = (pageIndex) => {
        setShowReview(false);
        setCurrentPage(pageIndex);
    };

    // Show review page if enabled
    if (showReview) {
        return (
            <DeclarationReview
                fieldValues={fieldValues}
                applicationType={
                    fields?.applicationType
                        ? {
                              ...fields.applicationType,
                              active_field_blocks: activeFieldBlocks || [],
                          }
                        : undefined
                }
                onEdit={handleEditFromReview}
                onSubmit={handleSubmitForm}
                isSubmitting={isSubmitting}
                submitError={submitError}
            />
        );
    }

    return (
        <div className='content-wrapper'>
            <div className="page-title-container">
                <div className="page-title-left">
                    <h1 className="page-title">
                        Заявление
                    </h1>
                    {fields?.applicationType && (
                        <Typography
                            variant="subtitle1"
                            color="text.secondary"
                            sx={{ mt: 1, fontSize: { xs: '14px', sm: '16px' } }}
                        >
                            Тип заявления: {fields.applicationType.name}
                        </Typography>
                    )}
                </div>
                <div className="page-title-right">
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: '12px', sm: '13px' } }}
                    >
                        Страница {currentPage + 1} из {totalPages}
                    </Typography>
                    {lastSavedAt && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5, display: 'block', fontSize: { xs: '11px', sm: '12px' } }}
                        >
                            Последнее сохранение: {moment(lastSavedAt).format('DD.MM.YYYY HH:mm')}
                            {isSavingDraft && ' (сохранение...)'}
                        </Typography>
                    )}
                </div>
            </div>

            {originalApplicationId && revisionAdminComment && (
                <Box sx={{ mb: 2 }}>
                    <Alert
                        severity="info"
                        sx={{
                            borderRadius: '16px',
                            backgroundColor: '#FFE8E3',
                            color: '#EF2E25',
                            border: '1px solid rgba(239, 46, 37, 0.18)',
                            '& .MuiAlert-icon': {
                                color: '#EF2E25',
                            },
                        }}
                    >
                        <Typography variant="subtitle2" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Комментарий по доработке заявления
                        </Typography>
                        <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                            {revisionAdminComment}
                        </Typography>
                    </Alert>
                </Box>
            )}
            
            {/* Error Alert */}
            {submitError && (
                <Box sx={{ mb: 2 }}>
                    <Alert severity="error" onClose={() => setSubmitError(null)}>
                        {submitError}
                    </Alert>
                </Box>
            )}
            
            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <CircularProgress />
                </Box>
            )}
            
            {/* Error State */}
            {error?.failed && !loading && (
                <Box sx={{ mb: 2 }}>
                    <Alert severity="error">
                        {error.message || 'Ошибка при загрузке данных формы'}
                    </Alert>
                </Box>
            )}
            
            <div className={styles.declarationBody}>
                {/* Show loading when fields are not yet loaded */}
                {(loading || !fields?.applicationType) && !error?.failed && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress sx={{ mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                            Загрузка данных формы...
                        </Typography>
                    </Box>
                )}
                
                {/* Show error if fields failed to load */}
                {error?.failed && !loading && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Alert severity="error">
                            {error.message || 'Ошибка при загрузке данных формы'}
                        </Alert>
                    </Box>
                )}
                
                {/* Show warning if no page data available */}
                {!loading && fields?.applicationType && !currentPageData && !error?.failed && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Alert severity="warning">
                            Не удалось загрузить данные для текущей страницы
                        </Alert>
                    </Box>
                )}
                
                {/* Render form fields only when everything is ready */}
                {!loading && fields?.applicationType && currentPageData && currentPageData.active_fields && currentPageData.active_fields.length > 0 && currentPageData.active_fields.map((field, index) => {
                    // Safety check: ensure field has required structure
                    if (!field || !field.field_type || !field.id) {
                        console.warn('Skipping invalid field:', field);
                        return null;
                    }
                    return (
                        <TextFields 
                            key={field.id || index}
                            fieldErrors={fieldErrors} 
                            allFieldsFilled={allFieldsFilled} 
                            setAllFieldsFilled={setAllFieldsFilled} 
                            fieldValues={fieldValues} 
                            field={field} 
                            onChange={handleInputChange} 
                            fileChange={(e) => inputFileChange(e, field)}
                        />
                    );
                })}

                <div className={styles.buttonContainer}>
                    <Button 
                        className={styles.button} 
                        onClick={handlePreviousPage} 
                        disabled={currentPage === 0 || isSubmitting}
                    >
                        Назад
                    </Button>
                    <div className={styles.actionsRow}>
                        <Button 
                            className={styles.button} 
                            onClick={handleSaveDraft}
                            disabled={isSubmitting || isSavingDraft}
                            variant="outlined"
                            size="small"
                        >
                            {isSavingDraft ? 'Сохранение...' : 'Сохранить черновик'}
                        </Button>
                        <Button 
                            className={styles.button + ' ' + styles.orange} 
                            onClick={handleNextPage}
                            disabled={isSubmitting}
                            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            {isSubmitting
                                ? 'Отправка...'
                                : (
                                    currentPage === totalPages - 1
                                        ? (originalApplicationId ? 'Проверить и отправить' : 'Проверить')
                                        : 'Далее'
                                  )
                            }
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Declaration;
